import type React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';

import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import user from 'js/lib/user';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import withAccomplishmentsandPaymentsRecommendationData from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationData';
import type { withAccomplishmentsAndPaymentsRecommendationDataProps } from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationData';
import type {
  AccomplishmentsPaymentsCourseQuery as AccomplishmentsPaymentsCourseQueryType,
  AccomplishmentsPaymentsCourseQueryVariables,
} from 'bundles/accomplishments/queries/__generated__/AccomplishmentsPaymentsCourseQuery';
import type {
  AccomplishmentsPaymentsRecommendationQuery as AccomplishmentsPaymentsRecommendationQueryType,
  AccomplishmentsPaymentsRecommendationQueryVariables,
  AccomplishmentsPaymentsRecommendationQuery_CourseAccomplishmentsV1Resource_byUser_elements as CourseAccomplishments,
} from 'bundles/accomplishments/queries/__generated__/AccomplishmentsPaymentsRecommendationQuery';
import type { Specialization, Specializations } from 'bundles/s12n-common/api/withSpecializationsData';
import withSpecializationsData from 'bundles/s12n-common/api/withSpecializationsData';
import { OnDemandSpecializationMembershipRole } from 'bundles/s12n-enroll/api/__generated__/globalTypes';
import type { S12nMemberships } from 'bundles/s12n-enroll/api/withS12nMembershipData';
import withS12nMembershipData from 'bundles/s12n-enroll/api/withS12nMembershipData';

type InputProps = {};
type GraphqlProps = {};
type GraphqlData = AccomplishmentsPaymentsRecommendationQueryType;
type GraphqlVariables = AccomplishmentsPaymentsRecommendationQueryVariables;
type GraphqlChildProps = {
  s12nData?: { s12nId: string; s12nName: string; s12nRemainingCourses: string[] }[];
  s12nUpNext?: string[][];
  recommendationDisplayPolicy?: string;
  s12nList: string[];
  completedCourseList: string[];
};

// This implements a switching logic among different Recommendation variations, which is expected to be used in A/B testings or user targeting
// Here, we generate an impression and switch among Recommendation variations.

export const DISPLAY_POLICY = {
  NO_COMPLETED_COURSES: 'no-completed-courses',
  COMPLETED_COURSE: 'completed-course',
};

const AccomplishmentsPaymentsRecommendationQuery = gql`
  query AccomplishmentsPaymentsRecommendationQuery($userId: String!) {
    CourseAccomplishmentsV1Resource {
      byUser(userId: $userId) {
        elements {
          test: id
          courseId
        }
      }
    }
  }
`;

export const isDayOneUser = (hasRec: boolean, hasCard?: boolean) => {
  return !!(hasRec || hasCard);
};

type CompletedCourses = CourseAccomplishments[];

type S12nEnrollments = {
  s12n: Specialization;
}[];

export const withRecommendationPolicySelector = withProps(
  (props: { completedCourses: CompletedCourses; s12nEnrollments: S12nEnrollments }) => {
    const { completedCourses, s12nEnrollments } = props;
    const hasCompleted = completedCourses.length > 0;
    let recommendationDisplayPolicy: string;
    const completedCourseList: string[] = [];

    let s12nList: string[] = [];
    const s12nData: {
      s12nId: string;
      s12nName: string;
      s12nRemainingCourses: string[];
    }[] = [];

    if (!hasCompleted) {
      recommendationDisplayPolicy = DISPLAY_POLICY.NO_COMPLETED_COURSES;
      return { recommendationDisplayPolicy, s12nList, s12nData, completedCourseList };
    }

    s12nEnrollments.forEach((specialization) => {
      const s12nId = specialization?.s12n?.id || '';
      const s12nName = specialization?.s12n?.name || '';
      const s12nRemainingCourses = specialization?.s12n?.courses.map((course) => course.id) || [];
      s12nList = s12nList.concat(s12nRemainingCourses);
      s12nData.push({
        s12nId,
        s12nName,
        s12nRemainingCourses,
      });
    });

    completedCourses.forEach((element) => {
      if (element) {
        completedCourseList.push(element.courseId);
      }
    });

    recommendationDisplayPolicy = DISPLAY_POLICY.COMPLETED_COURSE;
    return { recommendationDisplayPolicy, s12nList, s12nData, completedCourseList };
  }
);

type CourseInputProps = {};
type GraphqlCourseProps = { s12nList: string[] };
type GraphqlCourseData = AccomplishmentsPaymentsCourseQueryType;
type GraphqlCourseVariables = AccomplishmentsPaymentsCourseQueryVariables;
type GraphqlCourseChildProps = {};

const AccomplishmentsPaymentsCourseQuery = gql`
  query AccomplishmentsPaymentsCourseQuery($ids: [String!]!) {
    CoursesV1Resource {
      multiGet(ids: $ids) {
        elements {
          id
          slug
          partnerLogo
          partners {
            elements {
              name
              squareLogo
            }
          }
          imageSrc: photoUrl
          title: name
          membership {
            id
            s12n {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const upNextCourseSelector = ({
  data,
}: {
  data?: Partial<AccomplishmentsPaymentsCourseQueryType> & { loading?: boolean };
}): GraphqlCourseChildProps => {
  const courseData = data?.CoursesV1Resource?.multiGet?.elements;
  return { courseData };
};

// This determines a recommendation policy for a user
export const withRecommendationPolicy = compose<GraphqlChildProps, InputProps>(
  graphql<GraphqlProps, GraphqlData, GraphqlVariables, { completedCourses: CompletedCourses }>(
    AccomplishmentsPaymentsRecommendationQuery,
    {
      options: () => ({
        variables: { userId: `${user.get().id}` },
      }),
      props: ({ data }) => ({
        completedCourses:
          data?.CourseAccomplishmentsV1Resource?.byUser?.elements.flatMap((element) =>
            element !== null ? [element] : []
          ) ?? [],
      }),
    }
  )
);

// This pulls the remaining courses of a specialization
//
const upNextCourses = compose<GraphqlCourseChildProps, CourseInputProps>(
  graphql<GraphqlCourseProps, GraphqlCourseData, GraphqlCourseVariables, GraphqlCourseChildProps>(
    AccomplishmentsPaymentsCourseQuery,
    {
      options: ({ s12nList }) => ({
        variables: { ids: s12nList },
      }),
      props: upNextCourseSelector,
      skip: ({ s12nList }) => s12nList.length < 0,
    }
  )
);

const withUserIdAndRole = withProps(() => ({
  userId: user.get().id,
  role: OnDemandSpecializationMembershipRole.LEARNER,
}));

const withS12nIds = withProps((props: { s12nMemberships: S12nMemberships }) => ({
  ...props,
  s12nIds: props.s12nMemberships.map((membership) => membership.s12nId),
}));

export const withCombinedS12nData = withProps(
  (props: { s12nMemberships: S12nMemberships; specializations: Specializations }) => {
    const s12nEnrollments: S12nEnrollments = [];
    const { s12nMemberships, specializations } = props;
    for (const membership of s12nMemberships) {
      const { s12nId } = membership;
      const specialization = specializations.find(({ id }) => id === s12nId);
      if (specialization) {
        s12nEnrollments.push({
          ...membership,
          s12n: specialization,
        });
      }
    }
    return { ...props, s12nEnrollments };
  }
);

export default (Component: React.ComponentType<withAccomplishmentsAndPaymentsRecommendationDataProps>) =>
  compose<withAccomplishmentsAndPaymentsRecommendationDataProps, InputProps>(
    deferToClientSideRender(),
    withUserIdAndRole,
    withS12nMembershipData,
    withS12nIds,
    withSpecializationsData,
    withCombinedS12nData,
    withRecommendationPolicy,
    withRecommendationPolicySelector,
    upNextCourses,
    withAccomplishmentsandPaymentsRecommendationData
  )(Component);
